import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { ArticleSchema, WebPageSchema } from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="w-full px-4 md:px-8 py-20 text-center">
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-4">文章不存在</h1>
          <Link to="/" className="text-accent-500 hover:text-accent-600 font-medium">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === article.category)?.name || article.category;
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${article.title} - 黑料bu`}
        description={article.excerpt}
        keywords={`黑料bu,${article.tags.join(',')},${categoryName},黑料`}
        canonicalUrl={`https://heiliao.bu/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <ArticleSchema
        headline={article.title}
        url={`https://heiliao.bu/article/${article.id}`}
        description={article.excerpt}
        image={article.imageUrl}
        datePublished={article.date}
        dateModified={article.date}
        authorName={article.author}
      />
      <WebPageSchema
        name={`${article.title} - 黑料bu`}
        url={`https://heiliao.bu/article/${article.id}`}
        description={article.excerpt}
        datePublished={article.date}
        dateModified={article.date}
      />

      <Navbar />
      <Breadcrumb items={[
        { label: '首页', href: '/' },
        { label: categoryName, href: `/category/${article.category}` },
        { label: article.title },
      ]} />

      <main>
        <article className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full hover:bg-accent-200 transition-colors"
                >
                  {categoryName}
                </Link>
                {article.isHot && (
                  <span className="bg-accent-500 text-background-50 text-xs font-semibold px-2 py-0.5 rounded-md">
                    热门
                  </span>
                )}
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-md">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 border-b border-background-200 pb-4 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                </span>
              </div>
            </div>

            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 黑料bu`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200 mt-10 pt-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500">
                <span>声明：</span>
                <span>本文内容来源于网络公开信息，不代表黑料bu立场。如有不实信息，请联系我们处理。</span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-foreground-600 mb-4 text-sm">还想看更多黑料？</p>
            <Link
              to="/category/zuixin"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap"
            >
              浏览最新爆料 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}